import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: string;
  subscription_end: string | null;
  promo_code_used: string | null;
  monthlyEvents: number;
  monthlyGuests: number;
  familyMembers: number;
  isLoading: boolean;
}

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData>({
    subscribed: false,
    subscription_tier: 'free',
    subscription_end: null,
    promo_code_used: null,
    monthlyEvents: 0,
    monthlyGuests: 0,
    familyMembers: 0,
    isLoading: true
  });

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
    } else {
      setSubscription({
        subscribed: false,
        subscription_tier: 'free',
        subscription_end: null,
        promo_code_used: null,
        monthlyEvents: 0,
        monthlyGuests: 0,
        familyMembers: 0,
        isLoading: false
      });
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    if (!user) return;

    try {
      // Check subscription status
      const { data: subData } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      // Get monthly usage
      const { data: eventCount } = await supabase
        .rpc('get_monthly_event_count', { user_uuid: user.id });

  const { data: guestCount } = await supabase
    .rpc('get_monthly_guest_count', { user_uuid: user.id });

  const { data: familyMemberCount } = await supabase
    .rpc('get_family_member_count', { user_uuid: user.id });

  setSubscription({
    subscribed: subData?.subscribed || false,
    subscription_tier: subData?.subscription_tier || 'free',
    subscription_end: subData?.subscription_end || null,
    promo_code_used: subData?.promo_code_used || null,
    monthlyEvents: eventCount || 0,
    monthlyGuests: guestCount || 0,
    familyMembers: familyMemberCount || 0,
    isLoading: false
  });
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setSubscription(prev => ({ ...prev, isLoading: false }));
    }
  };

  const checkCanCreateEvent = () => {
    const tier = subscription.subscription_tier;
    
    // Professional and Enterprise have unlimited events
    if (tier === 'professional' || tier === 'enterprise') return { canCreate: true, reason: '' };
    
    // Family plan: 10 events per month
    if (tier === 'family') {
      if (subscription.monthlyEvents >= 10) {
        return { 
          canCreate: false, 
          reason: 'You have reached your monthly limit of 10 events. Upgrade to Professional for unlimited events!' 
        };
      }
      return { canCreate: true, reason: '' };
    }
    
    // Free plan: 3 events per month
    if (subscription.monthlyEvents >= 3) {
      return { 
        canCreate: false, 
        reason: 'You have reached your monthly limit of 3 events. Upgrade to Family or Professional for more events!' 
      };
    }
    
    return { canCreate: true, reason: '' };
  };

  const checkCanAddGuests = (currentGuests: number) => {
    const tier = subscription.subscription_tier;
    
    // Professional and Enterprise have unlimited guests
    if (tier === 'professional' || tier === 'enterprise') return { canAdd: true, reason: '' };
    
    // Family plan: 50 guests per event
    if (tier === 'family') {
      if (currentGuests > 50) {
        return { 
          canAdd: false, 
          reason: 'Family plan allows up to 50 guests per event. Upgrade to Professional for unlimited guests!' 
        };
      }
      return { canAdd: true, reason: '' };
    }
    
    // Free plan: 10 guests per event
    if (currentGuests > 10) {
      return { 
        canAdd: false, 
        reason: 'Free plan allows up to 10 guests per event. Upgrade to Family or Professional for more guests!' 
      };
    }
    
    return { canAdd: true, reason: '' };
  };

  const checkCanAddFamilyMembers = () => {
    const tier = subscription.subscription_tier;
    
    // Family, Professional, and Enterprise have unlimited family members
    if (tier === 'family' || tier === 'professional' || tier === 'enterprise') {
      return { canAdd: true, reason: '' };
    }
    
    // Free plan: Limited family members
    if (subscription.familyMembers >= 5) {
      return { 
        canAdd: false, 
        reason: 'You have reached your limit of 5 family members. Upgrade to Family Plan for unlimited members!' 
      };
    }
    
    return { canAdd: true, reason: '' };
  };

  // Feature access checks
  const hasClientPortalAccess = () => {
    return ['professional', 'enterprise'].includes(subscription.subscription_tier);
  };

  const hasAdvancedAnalytics = () => {
    return subscription.subscription_tier === 'enterprise';
  };

  const hasCustomBranding = () => {
    return ['professional', 'enterprise'].includes(subscription.subscription_tier);
  };

  const hasAdvancedBranding = () => {
    return subscription.subscription_tier === 'enterprise';
  };

  const hasSmartFeatures = () => {
    return subscription.subscription_tier === 'enterprise';
  };

  const hasUnlimitedCustomRSVP = () => {
    return ['professional', 'enterprise'].includes(subscription.subscription_tier);
  };

  const hasBasicCustomRSVP = () => {
    return ['family', 'professional', 'enterprise'].includes(subscription.subscription_tier);
  };

  const getCustomRSVPLimit = () => {
    if (hasUnlimitedCustomRSVP()) return -1; // Unlimited
    if (hasBasicCustomRSVP()) return 3; // Family plan limit
    return 0; // Free plan
  };

  const checkSubscription = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) throw error;
      
      setSubscription(prev => ({
        ...prev,
        subscribed: data.subscribed,
        subscription_tier: data.subscription_tier,
        subscription_end: data.subscription_end,
        isLoading: false
      }));
      
      // Also fetch fresh data from database to ensure consistency
      await fetchSubscriptionData();
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Only show error toast if it's not a silent refresh
      if (window.location.search.includes('success') || window.location.search.includes('canceled')) {
        // Don't show error toast during redirect handling
        return;
      }
      toast.error('Failed to check subscription status');
    }
  };

  const createCheckout = async (priceId: string) => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      return;
    }

    try {
      // Get the current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Please sign in to subscribe');
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout session');
    }
  };

  const openCustomerPortal = async () => {
    if (!user) {
      toast.error('Please sign in to manage subscription');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw error;

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open customer portal');
    }
  };

  const refreshSubscription = () => {
    fetchSubscriptionData();
  };

  return {
    subscription,
    subscribed: subscription.subscribed,
    subscription_tier: subscription.subscription_tier,
    subscription_end: subscription.subscription_end,
    checkCanCreateEvent,
    checkCanAddGuests,
    checkCanAddFamilyMembers,
    refreshSubscription,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    // Feature access checks
    hasClientPortalAccess,
    hasAdvancedAnalytics,
    hasCustomBranding,
    hasAdvancedBranding,
    hasSmartFeatures,
    hasUnlimitedCustomRSVP,
    hasBasicCustomRSVP,
    getCustomRSVPLimit
  };
};